import { Button, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import React, { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatNumber,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import { FactsTable, FactsTableRow, FactsTableRowFlexibleColumns } from '../components/FactsTable'
import { findSubscriptionPeriodCloseToDate, getAmountForPriceAtDate, hasSubPeriodPrelPrice } from '../../utils/warrants'
import { WarrantEntity } from '../../types/WarrantEntity'

const WarrantTerms = ({ warrant }: { warrant: WarrantEntity }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const tableRows: ReactNode[] = []

    const sharesText = { singular: 'aktie', plural: 'aktier' }
    const warrantsText = { singular: 'TO', plural: 'TO' }
    const quantityLabel = (
        quantity: number | null,
        text: {
            singular: string
            plural: string
        },
    ) => {
        return `${formatNumber(quantity, { locale, decimals: 0 })} ${quantity === 1 ? text.singular : text.plural}`
    }

    const subscriptionPeriods = warrant.subscriptionPeriods
    const [startPeriodIndex, setStartPeriodIndex] = React.useState(0)

    useEffect(() => {
        const currentPeriod = findSubscriptionPeriodCloseToDate({ subscriptionPeriods }, new Date().toISOString())
        setStartPeriodIndex(subscriptionPeriods.findIndex((subPeriod) => subPeriod.id === currentPeriod?.id))
    }, [subscriptionPeriods])

    const subscriptionPeriodsRange = subscriptionPeriods.slice(startPeriodIndex, startPeriodIndex + 1)

    if (subscriptionPeriods && subscriptionPeriods.length > 0) {
        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Teckningsperiod'}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const startDate = formatDateYYYYMMDD(subscriptionPeriod.subStartDate)
                    const endDate = formatDateYYYYMMDD(subscriptionPeriod.subEndDate)
                    return `${startDate} - ${endDate}`
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Ratio'}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) =>
                    `${quantityLabel(subscriptionPeriod.warrantsRequired, warrantsText)} => ${quantityLabel(
                        subscriptionPeriod.sharesReceived,
                        sharesText,
                    )}`
                }
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Teckningskurs'}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const { warrantSubPrice, prelSubPrice, sharesReceived } = subscriptionPeriod
                    const showPrelPrice = hasSubPeriodPrelPrice(subscriptionPeriod)
                    let formattedMoney = formatMoney(showPrelPrice ? prelSubPrice : warrantSubPrice, {
                        minDecimals: 2,
                        maxDecimals: 5,
                        locale,
                    })
                    if (formattedMoney && sharesReceived && sharesReceived > 1) {
                        formattedMoney = `${formattedMoney} per aktie`
                    }

                    return (
                        <>
                            {formattedMoney || '-'}
                            {showPrelPrice && '*'}
                        </>
                    )
                }}
                lastColumnValue={null}
            />,
        )

        if (warrant.closePrice !== null) {
            tableRows.push(
                <FactsTableRowFlexibleColumns
                    key={tableRows.length}
                    firstColumnValue={'Aktuell aktiekurs'}
                    columns={subscriptionPeriodsRange}
                    getColumnValue={() => {
                        return (
                            <>
                                {formatMoney(warrant.closePrice, {
                                    minDecimals: 2,
                                    maxDecimals: 5,
                                    locale,
                                })}
                            </>
                        )
                    }}
                    lastColumnValue={null}
                />,
            )
        }

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Belopp vid full nyttjandegrad'}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const { subscriptionPeriods, totalNumberOfWarrants } = warrant
                    const { warrantSubPrice, prelSubPrice, subStartDate } = subscriptionPeriod
                    const showPrelPrice = hasSubPeriodPrelPrice(subscriptionPeriod)
                    const price = showPrelPrice ? prelSubPrice : warrantSubPrice
                    const amount =
                        subStartDate === null
                            ? null
                            : getAmountForPriceAtDate(
                                  price,
                                  totalNumberOfWarrants,
                                  subscriptionPeriods,
                                  new Date(subStartDate).toISOString(),
                              )

                    return (
                        <>
                            {formatBigMoney(amount, {
                                currencySymbol: warrant.currency,
                                decimals: 0,
                                locale,
                                tooltip: true,
                            }) || '-'}
                            {showPrelPrice && '*'}
                        </>
                    )
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Prissättning'}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const vwapDays =
                        subscriptionPeriod.vwapDays !== null && subscriptionPeriod.vwapDays > 0
                            ? subscriptionPeriod.vwapDays
                            : 0
                    return (
                        <>
                            {!!subscriptionPeriod.isVwap && (
                                <div>
                                    <Typography variant='body1'>
                                        {`VWAP-rabatt: ${formatPercentage(subscriptionPeriod?.vwapDiscount, {
                                            locale,
                                            minDecimals: 0,
                                        })}${vwapDays > 0 ? ` (${vwapDays} ${vwapDays > 1 ? 'dagar' : 'dag'})` : null}`}
                                    </Typography>
                                    <Typography variant='body1'>
                                        Min:{' '}
                                        {subscriptionPeriod?.priceMin
                                            ? formatMoney(subscriptionPeriod?.priceMin, {
                                                  minDecimals: 2,
                                                  maxDecimals: 5,
                                                  locale,
                                              })
                                            : 'kvotvärde'}
                                        , Max:{' '}
                                        {subscriptionPeriod?.priceMax
                                            ? formatMoney(subscriptionPeriod?.priceMax, {
                                                  minDecimals: 2,
                                                  maxDecimals: 5,
                                                  locale,
                                              })
                                            : '-'}
                                    </Typography>
                                </div>
                            )}
                            {!subscriptionPeriod.isVwap && <span>Fast kurs</span>}
                        </>
                    )
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Säkerställd'}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const hasCommitments = subscriptionPeriod.commitments != null
                    const hasGuarantors = subscriptionPeriod.guarantors != null

                    if (hasCommitments || hasGuarantors) {
                        return (
                            <div>
                                {hasCommitments && (
                                    <Typography variant='body1'>
                                        Teckningsåtagande:{' '}
                                        {formatPercentage(subscriptionPeriod.commitments, {
                                            locale,
                                            minDecimals: 0,
                                        })}
                                    </Typography>
                                )}
                                {hasGuarantors && (
                                    <Typography variant='body1'>
                                        Garantiåtagande:{' '}
                                        {formatPercentage(subscriptionPeriod.guarantors, {
                                            locale,
                                            minDecimals: 0,
                                        })}
                                    </Typography>
                                )}
                            </div>
                        )
                    }
                }}
                lastColumnValue={null}
            />,
        )

        const hasPrelPrice = subscriptionPeriodsRange.find(hasSubPeriodPrelPrice)

        if (hasPrelPrice) {
            const vwapDays =
                subscriptionPeriodsRange.length &&
                subscriptionPeriodsRange[0]?.vwapDays !== null &&
                subscriptionPeriodsRange[0]?.vwapDays > 0
                    ? subscriptionPeriodsRange[0].vwapDays
                    : 0
            if (vwapDays <= 1) {
                tableRows.push(
                    <FactsTableRow
                        key='preliminary-price-info'
                        label={`* Preliminärt baserat på senaste handelsdagen`}
                    />,
                )
            } else {
                tableRows.push(
                    <FactsTableRow
                        key='preliminary-price-info'
                        label={`* Preliminärt baserat på de senaste ${vwapDays} handelsdagarna`}
                    />,
                )
            }
        }
    }

    if (tableRows.length === 0) {
        return (
            <div>
                <Typography variant='body1'>Inga underliggande perioder hittades</Typography>
            </div>
        )
    }

    return (
        <div>
            <FactsTable rows={tableRows} />
            {subscriptionPeriods.length > 1 && (
                <>
                    <Stack
                        direction={'row'}
                        sx={{
                            justifyContent: 'space-between',
                            my: 6,
                        }}
                    >
                        <Button
                            disabled={startPeriodIndex === 0}
                            onClick={() => {
                                if (startPeriodIndex > 0) setStartPeriodIndex(startPeriodIndex - 1)
                            }}
                        >
                            Föregående
                        </Button>
                        <Button
                            disabled={startPeriodIndex === subscriptionPeriods.length - 1}
                            onClick={() => {
                                if (startPeriodIndex < subscriptionPeriods.length - 1)
                                    setStartPeriodIndex(startPeriodIndex + 1)
                            }}
                        >
                            Nästa
                        </Button>
                    </Stack>
                    <Typography variant='body1'>
                        Visar period {startPeriodIndex + 1} av totalt {subscriptionPeriods.length} teckningsperioder.
                    </Typography>
                </>
            )}
        </div>
    )
}

export default WarrantTerms
